// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { AlertTriangle, CheckCircle, ChevronDown, Download, X } from "lucide-react";
import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { formatCurrency } from "@crm-src/lib/utils";
import { formatDate, formatServicePeriod } from "@crm-src/lib/date-utils";
import type { Invoice } from "@crm-src/lib/store/invoicesSlice";

interface InvoiceDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: Invoice | null;
  isQuote: (invoice: any) => boolean;
  onMarkAsPaid: (invoice: Invoice) => void;
  onMarkAsUnpaid: (invoice: Invoice) => void;
  onDownload: (invoice: Invoice) => void;
}

export function InvoiceDetailsDialog({
  open,
  onOpenChange,
  invoice,
  isQuote,
  onMarkAsPaid,
  onMarkAsUnpaid,
  onDownload,
}: InvoiceDetailsDialogProps) {
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);

  return (
    <Dialog open={open} onClose={() => onOpenChange(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: "1.5rem", fontWeight: 700, textAlign: "center" }}>
        {invoice?.invoice_number || 'Invoice Details'}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontSize: "1.125rem", color: "text.secondary", textAlign: "center", mb: 1 }}>
          {invoice?.customer?.company_name || 'N/A'}
        </DialogContentText>
        {invoice && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Status Badges */}
            <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
              {isQuote(invoice) ? (
                <Chip
                  color="primary"
                  label="QUOTE"
                  sx={{ fontSize: "1.125rem", px: 2, py: 0.5, fontWeight: 600 }}
                />
              ) : (
                <>
                  <Chip
                    color={invoice.payment_status === 'paid' ? 'success' : 'error'}
                    label={(invoice.payment_status || 'unpaid').toUpperCase()}
                    sx={{ fontSize: "1.125rem", px: 2, py: 0.5, fontWeight: 600 }}
                  />

                  {(invoice as any).overdue && (
                    <Chip
                      color="error"
                      icon={<AlertTriangle size={16} />}
                      label="OVERDUE"
                      sx={{ fontSize: "1.125rem", px: 2, py: 0.5, fontWeight: 600, animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite", "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.4 } } }}
                    />
                  )}
                </>
              )}
            </Box>

            <Divider />

            {/* Customer Information */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography sx={{ fontSize: "1rem", fontWeight: 500 }}>{invoice.customer_name || 'N/A'}</Typography>
              {invoice.customer_email && (
                <Typography sx={{ fontSize: 14, color: "text.secondary" }}>{invoice.customer_email}</Typography>
              )}
            </Box>

            {/* Invoice Amount */}
            <Box sx={{ bgcolor: "surfaceSoft", borderRadius: 1, p: 2, textAlign: "center" }}>
              <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary", mb: 0.5 }}>Invoice Amount</Typography>
              <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "text.primary" }}>
                {formatCurrency(invoice.total_amount || 0)}
              </Typography>
            </Box>

            {/* Dates */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2, fontSize: 14 }}>
              <Box>
                <Typography sx={{ fontWeight: 500, fontSize: 14 }}>Issue Date</Typography>
                <Typography sx={{ color: "text.secondary", fontSize: 14 }}>{formatDate(invoice.issue_date)}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 500, fontSize: 14 }}>Due Date</Typography>
                <Typography sx={{ color: "text.secondary", fontSize: 14 }}>{formatDate(invoice.due_date)}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 500, fontSize: 14, color: "success.dark" }}>Paid Date</Typography>
                <Typography sx={{ color: "success.main", fontSize: 14 }}>
                  {(invoice as any).paid_date ? formatDate((invoice as any).paid_date) : '-'}
                </Typography>
              </Box>
            </Box>

            {/* Service Period — invoice-only (quotes have no service window),
                shown only when at least one end is set. Sits right under the
                dates, alongside the invoice number in the header. */}
            {!isQuote(invoice) && formatServicePeriod(invoice.service_from, invoice.service_to) && (
              <Box sx={{ fontSize: 14 }}>
                <Typography sx={{ fontWeight: 500, fontSize: 14 }}>Service Period</Typography>
                <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
                  {formatServicePeriod(invoice.service_from, invoice.service_to)}
                </Typography>
              </Box>
            )}

            <Divider />

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 1 }}>
              {!isQuote(invoice) && (
                <>
                  <Button
                    variant="outlined"
                    color="inherit"
                    sx={{ flex: 1 }}
                    onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
                    endIcon={<ChevronDown size={16} />}
                  >
                    Payment Status
                  </Button>
                  <Menu
                    anchorEl={statusMenuAnchor}
                    open={Boolean(statusMenuAnchor)}
                    onClose={() => setStatusMenuAnchor(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    transformOrigin={{ vertical: "top", horizontal: "center" }}
                  >
                    <MenuItem
                      onClick={() => {
                        setStatusMenuAnchor(null);
                        if (invoice) {
                          onMarkAsPaid(invoice);
                        }
                      }}
                    >
                      <Box sx={{ mr: 1, display: "flex" }}><CheckCircle size={16} /></Box>
                      Mark as Paid
                    </MenuItem>
                    <MenuItem
                      disabled={invoice.payment_status === 'unpaid'}
                      onClick={() => {
                        setStatusMenuAnchor(null);
                        if (invoice) {
                          onMarkAsUnpaid(invoice);
                        }
                      }}
                    >
                      <Box sx={{ mr: 1, display: "flex" }}><X size={16} /></Box>
                      Mark as Unpaid
                    </MenuItem>
                  </Menu>
                </>
              )}

              <Button
                variant="outlined"
                color="inherit"
                sx={{ flex: 1 }}
                onClick={() => {
                  if (invoice) {
                    onDownload(invoice);
                  }
                }}
                startIcon={<Download size={16} />}
              >
                Download
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
